import type { IEditorModel } from '@next-space/fe-inlined';
import {
  contentToString,
  findItemPathInRange,
  getElementByPath,
  getItemLength,
  newContent,
  newText,
  sliceContent,
} from '@next-space/fe-inlined/utils';
import isHotkey from 'is-hotkey';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LinkWidget } from 'src/common/components/link-widget';
import type { ModalSchema } from 'src/common/components/next-modal';
import { useOpenModal } from 'src/common/components/next-modal';
import { LINK_TAG } from 'src/editor/editor/inline/const';
import { useGetOrInitEditorModel } from 'src/editor/editor/uikit/editable/hook';
import { normalizeHref } from 'src/utils/embed';

interface Option {
  model?: IEditorModel;
  setFakeFocus?: (status: boolean) => void;
}
export const useShortcutLink = (uuid: string, option?: Option) => {
  const getEditorModel = useGetOrInitEditorModel();
  const { model: _model, setFakeFocus } = option || {};
  const linkUrlRef = useRef('');
  const linkTitleRef = useRef('');
  const originalTitleRef = useRef('');
  const openModal = useOpenModal();

  return useCallback(
    (edit?: boolean) => {
      const model = _model || getEditorModel(uuid);
      if (!model) return;

      const selection = model.active ? model.selection : null;
      if (!selection) return;

      const findLinkPath = (content: typeof model.content) =>
        findItemPathInRange(content, selection.offset, selection.endOffset, (item, offset) => {
          if (item.type === 'element' && item.tag === LINK_TAG) {
            if (selection.isCollapsed) {
              return true;
            }
            const endOffset = offset + getItemLength(item);
            return selection.endOffset > offset && selection.offset < endOffset;
          }
          return false;
        });

      const linkPath = findLinkPath(model.content);
      const linkElement = linkPath.length > 0 ? getElementByPath(model.content, linkPath) : null;
      const inLink = linkElement ? (linkElement.props.url as string) : null;

      // 提取链接文本或选区文本作为标题
      let linkText = '';
      if (linkElement && linkElement.content) {
        linkText = contentToString(linkElement.content);
      } else if (!selection.isCollapsed) {
        linkText = contentToString(
          sliceContent(model.content, selection.offset, selection.endOffset)
        );
      }

      linkUrlRef.current = inLink ?? '';
      linkTitleRef.current = linkText;
      originalTitleRef.current = linkText;

      const LinkModel: ModalSchema.Content = ({ onCloseModal }) => {
        const [linkUrl, setLinkUrl0] = useState(inLink ?? '');
        const [localLinkTitle, setLocalLinkTitle0] = useState(linkText);

        const setLinkUrl = (url: string) => {
          setLinkUrl0(url);
          linkUrlRef.current = url;
        };
        const setLinkTitle = (title: string) => {
          setLocalLinkTitle0(title);
          linkTitleRef.current = title;
        };

        useEffect(() => {
          const handleKeydown = (event: KeyboardEvent) => {
            if (isHotkey('esc')(event)) {
              linkUrlRef.current = '';
            }
          };
          document.addEventListener('keydown', handleKeydown, { capture: true });
          setFakeFocus?.(true);
          return () => {
            setFakeFocus?.(false);
            document.removeEventListener('keydown', handleKeydown, { capture: true });
          };
        }, []);

        return (
          <LinkWidget
            inLink={edit === undefined ? inLink !== null : false}
            link={linkUrl}
            linkTitle={localLinkTitle}
            onLinkChange={setLinkUrl}
            onLinkTitleChange={setLinkTitle}
            onCancel={() => {
              linkUrlRef.current = '';
            }}
            onClearLink={() => {
              setLinkUrl('');
              model.performChange((ctx) => {
                const currentLinkPath = findLinkPath(model.content);
                ctx.select(selection.offset, selection.endOffset);
                if (currentLinkPath.length > 0) {
                  ctx.unwrap(LINK_TAG, currentLinkPath);
                }
              });
            }}
            onCloseModal={onCloseModal}
          />
        );
      };
      openModal.dropdown({
        popcorn: {
          getBoundingClientRect: () => {
            const rect = model.getBoundingClientRectOfRange(selection.offset, selection.endOffset);
            return rect ?? new DOMRect(-9999, -9999, 0, 0);
          },
        },
        closeAfterCallBack: () => {
          void model.requestFocus();
        },
        closeBeforeCallBack: () => {
          const linkUrl = linkUrlRef.current;
          const linkTitle = linkTitleRef.current;
          const originalTitle = originalTitleRef.current;
          const href = normalizeHref(linkUrl);

          if (href === '') return;

          const titleChanged = linkTitle !== originalTitle;

          model.performChange((ctx) => {
            ctx.select(selection.offset, selection.endOffset);
            if (ctx.selection === null) return;

            if (titleChanged) {
              // 标题发生变化：先解除旧链接包裹，删除旧文本，再插入新链接元素
              const currentLinkPath = findLinkPath(model.content);
              if (currentLinkPath.length > 0) {
                ctx.unwrap(LINK_TAG, currentLinkPath);
              }
              ctx.select(selection.offset, selection.endOffset);
              ctx.delete();
              ctx.insert(
                newContent([
                  {
                    type: 'element',
                    tag: LINK_TAG,
                    props: { url: href },
                    content: newContent([newText(linkTitle, ctx.format)]),
                  },
                ])
              );
            } else if (ctx.selection.isCollapsed) {
              // 光标折叠：插入新链接元素，标题默认为 URL
              const title = linkTitle || href;
              ctx.insert(
                newContent([
                  {
                    type: 'element',
                    tag: LINK_TAG,
                    props: { url: href },
                    content: newContent([newText(title, ctx.format)]),
                  },
                ])
              );
            } else {
              // 标题未变化且有选区：仅用新 URL 包裹
              ctx.wrap(LINK_TAG, {
                url: href,
              });
            }
          });
        },
        placement: 'bottom',
        offset: [4, 4],
        content: LinkModel,
      });
    },
    [_model, getEditorModel, openModal, setFakeFocus, uuid]
  );
};
